import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { analytics, identifyUser } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

// Decode JWT payload to extract user_id (fallback for existing sessions)
function decodeJwtPayload(token: string | null): { user_id?: number } | null {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return payload;
  } catch {
    return null;
  }
}

// User data returned from login
export interface AuthUser {
  id: number;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  photoUrl: string | null;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  error: string | null;
}

// Load persisted state from localStorage
const accessToken = localStorage.getItem('accessToken');
const refreshToken = localStorage.getItem('refreshToken');
const storedUser = localStorage.getItem('authUser');
const user = storedUser ? JSON.parse(storedUser) as AuthUser : null;

const initialState: AuthState = {
  accessToken: accessToken,
  refreshToken: refreshToken,
  user: user,
  isLoggedIn: !!accessToken,
  isLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
      state.isLoggedIn = !!action.payload;
      localStorage.setItem('accessToken', action.payload);
    },
    setRefreshToken: (state, action: PayloadAction<string>) => {
      state.refreshToken = action.payload;
      localStorage.setItem('refreshToken', action.payload);
    },
    setUser: (state, action: PayloadAction<AuthUser>) => {
      state.user = action.payload;
      localStorage.setItem('authUser', JSON.stringify(action.payload));
      // Identify user in analytics (only if id exists)
      if (action.payload.id != null) {
        identifyUser(action.payload);
      }
    },
    updateUserPhoto: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.photoUrl = action.payload;
        localStorage.setItem('authUser', JSON.stringify(state.user));
      }
    },
    clearAuth: (state) => {
      // Track logout before clearing state
      analytics.capture(AnalyticsEvents.LOGOUT);
      analytics.reset();

      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      state.isLoggedIn = false;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('authUser');
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setAccessToken,
  setRefreshToken,
  setUser,
  updateUserPhoto,
  clearAuth,
  setLoading,
  setError,
} = authSlice.actions;

// Selectors
export const selectIsLoggedIn = (state: { auth: AuthState }) => state.auth.isLoggedIn;
// Get user ID from user object, or fallback to decoding JWT for existing sessions
export const selectCurrentUserId = (state: { auth: AuthState }) =>
  state.auth.user?.id ?? decodeJwtPayload(state.auth.accessToken)?.user_id ?? null;
export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;

export const authReducer = authSlice.reducer;
